import React from 'react';
import { Button, CustomSelect, CustomSelectItem, LabeledValue, LoadingData } from '@hwm/ui-lib';
import { Collapse, makeStyles, Theme } from '@material-ui/core';
import { t, Trans } from '@lingui/macro';
import { DialogModal } from '../DialogModal';
import { useLoading } from '@shared/hooks/useLoading';

const useStyles = makeStyles((theme: Theme) => ({
  select: {
    height: 36,
    maxHeight: 36,
    width: 180,
    maxWidth: 180,
    zIndex: 200,
    marginRight: theme.spacing(2),
    '& .select__control': {
      minWidth: 108,
    },
    [theme.breakpoints.down('xs')]: {
      width: 160,
      maxWidth: 160,
    },
  },
  labeledValue: {
    marginBottom: theme.spacing(10),
    [theme.breakpoints.down('xs')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
  },
}));

export interface ExportData {
  label: string;
  onChange: (selectedValue: CustomSelectItem | CustomSelectItem[]) => void;
  options: string[];
  value: string;
  onExportClick: () => void;
  hideButton?: boolean;
  displaySingleOption?: boolean;
}

interface ExportModalProps {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  btnDisabled: boolean;
  dataLoading?: boolean;
  exportLoading?: boolean;
  exportsData: ExportData[];
  /**
   * `dataTestId` name of a modal (preferably `<name>-export-modal`)
   * @example
   * ```tsx
   * <ExportModal {...props} dataTestId="test-export-modal" />
   */
  dataTestId?: string;
}

export const ExportModal: React.FC<ExportModalProps> = ({
  show,
  setShow,
  title,
  btnDisabled,
  dataLoading = false,
  exportLoading,
  exportsData,
  dataTestId,
}) => {
  const classes = useStyles();

  const [loading] = useLoading(exportLoading, 1000);

  return (
    <>
      <Button
        style={{
          height: 28,
          padding: 8,
        }}
        onClick={() => setShow(true)}
        disabled={btnDisabled}
        dataTestId={`${dataTestId}-button`}
      >
        <Trans>Export</Trans>
      </Button>
      <DialogModal show={show} setShow={setShow} headerTitle={title} headerDivider dataTestId={dataTestId}>
        <>
          {dataLoading && <LoadingData />}
          <Collapse in={!dataLoading}>
            <div className="flex flex-col m-2 -mb-2" data-testid={`${dataTestId}-content`}>
              {exportsData.map((el, i) => (
                <LabeledValue
                  key={el.label}
                  label={el.label}
                  className={classes.labeledValue}
                  dataTestId={`${dataTestId}-content-item-${i}-labeled-value`}
                >
                  <div className="flex items-center sm:ml-2">
                    {(el.options?.length > 1 || (el.options?.length === 1 && el.displaySingleOption)) && (
                      <CustomSelect
                        label={null}
                        placeholder={t`Select file type`}
                        onChange={el.onChange}
                        options={el.options?.map((el) => ({
                          label: el,
                          value: el,
                        }))}
                        minWidth={108}
                        className={classes.select}
                        value={
                          el.value
                            ? ({
                                label: el.value,
                                value: el.value,
                              } as CustomSelectItem)
                            : null
                        }
                        isDisabled={!Boolean(el.value)}
                        isError={false}
                        newEditableLook
                        dataTestId={`${dataTestId}-content-item-${i}-labeled-value-export-type-select`}
                      />
                    )}
                    {!el.hideButton && (
                      <Button
                        style={{
                          width: 88,
                          padding: 0,
                        }}
                        onClick={() => (!loading ? el?.onExportClick() : null)}
                        loading={loading}
                        disabled={!Boolean(el.value) || loading || dataLoading}
                        dataTestId={`${dataTestId}-content-item-${i}-labeled-value-export-button`}
                      >
                        <Trans>Export</Trans>
                      </Button>
                    )}
                  </div>
                </LabeledValue>
              ))}
            </div>
          </Collapse>
        </>
      </DialogModal>
    </>
  );
};
